import { Portal, Tooltip as ChakraTooltip } from '@chakra-ui/react';
import * as React from 'react';

export interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean;
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
  content: React.ReactNode;
  contentProps?: ChakraTooltip.ContentProps;
  disabled?: boolean;
}

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(props, ref) {
  const {
    showArrow,
    children,
    disabled,
    portalled = true,
    content,
    contentProps,
    portalRef,
    ...rest
  } = props;
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const [touchOpen, setTouchOpen] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse)');
    const update = () => {
      setIsTouchDevice(mediaQuery.matches);
    };

    update();
    mediaQuery.addEventListener('change', update);

    return () => {
      mediaQuery.removeEventListener('change', update);
    };
  }, []);

  const handleOpenChange: ChakraTooltip.RootProps['onOpenChange'] = (details) => {
    if (isTouchDevice) {
      setTouchOpen(details.open);
    }

    rest.onOpenChange?.(details);
  };

  const handleTriggerClick = React.useCallback(() => {
    if (!isTouchDevice) return;

    setTouchOpen((prev) => !prev);
  }, [isTouchDevice]);

  const triggerChild = React.isValidElement<{ onClick?: (...args: unknown[]) => void }>(children) ? (
    React.cloneElement(children, {
      onClick: (...args: unknown[]) => {
        children.props.onClick?.(...args);
        handleTriggerClick();
      },
    })
  ) : (
    <span onClick={handleTriggerClick}>{children}</span>
  );

  if (disabled) return children;

  return (
    <ChakraTooltip.Root
      {...rest}
      open={isTouchDevice ? touchOpen : rest.open}
      onOpenChange={handleOpenChange}
    >
      <ChakraTooltip.Trigger asChild>{triggerChild}</ChakraTooltip.Trigger>
      <Portal disabled={!portalled} container={portalRef}>
        <ChakraTooltip.Positioner>
          <ChakraTooltip.Content ref={ref} {...contentProps} bg="white" color="body.500">
            {showArrow && (
              <ChakraTooltip.Arrow>
                <ChakraTooltip.ArrowTip bg="!white" borderColor="!gray.200" />
              </ChakraTooltip.Arrow>
            )}
            {content}
          </ChakraTooltip.Content>
        </ChakraTooltip.Positioner>
      </Portal>
    </ChakraTooltip.Root>
  );
});
